import { Routes, Route, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import LoginPage from "./pages/Login/LoginPage";
import Dashboard from "./pages/Dashboard/Dashboard";
import SignupPage from "./pages/SignUp/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPassword/ForgotPasswordPage";
import useAuthStore from "./store/authStore";
import { initSocket } from "./services/socket";
import ProtectedRoute from "./utils/protectedRoute";
import UnProtectedRoute from "./utils/unProtectedRoute";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import api from "./services/api";
import { ENDPOINTS } from "./utils/endpoints";
import useDashboardStore from "./store/dashboardStore";

function App() {
  const { socket, logout, isAuthenticated, userMeta, updateMembershipDetails } =
    useAuthStore();
  const { setIsUS30Choosed } = useDashboardStore();
  const navigate = useNavigate();

  // Add membership trigger state
  const [membershipTrigger, setMembershipTrigger] = useState(0);

  // Reconnect socket after refresh and validate the session
  useEffect(() => {
    const getSessionToken = async () => {
      try {
        const { data } = await api.post(ENDPOINTS.SESSION, {
          userId: userMeta.wordpress_user_id,
        });
        if (data.success) {
          let sessionId = data.session;
          if (userMeta?.session_id !== sessionId) {
            toast.error(data.reason || "You have been logged out.");
            logout();
            navigate("/");
          }
        }
      } catch (err) {
        console.error("Failed to fetch session", err);
      }
    };

    const getMembershipIds = async () => {
      try {
        const { data } = await api.post(ENDPOINTS.MEMBERSHIP, {
          userId: userMeta.wordpress_user_id,
        });
        if (data.success) {
          updateMembershipDetails(
            userMeta,
            data.is_membership_active,
            data.membership_ids
          );
        }
      } catch (err) {
        console.error("Failed to fetch membership", err);
      }
    };

    if (isAuthenticated && !socket && userMeta?.session_id) {
      initSocket(userMeta.session_id);
      getSessionToken();
      getMembershipIds();
    }
  }, [
    isAuthenticated,
    socket,
    userMeta,
    logout,
    navigate,
    setIsUS30Choosed,
    updateMembershipDetails,
  ]);

  // Handle force logout from socket
  useEffect(() => {
    if (!socket) return;

    const handleForceLogout = (data) => {
      toast.error(data.reason || "You have been logged out.");
      logout();
      navigate("/");
    };

    socket.on("forceLogout", handleForceLogout);

    return () => {
      socket.off("forceLogout", handleForceLogout);
    };
  }, [socket, logout, navigate]);

  // Handle membership update from socket
  useEffect(() => {
    if (!socket) return;

    const handleMembershipUpdate = (data) => {
      updateMembershipDetails(
        userMeta,
        data.is_membership_active,
        data.membership_ids
      );

      // Trigger auto-join to re-run
      setMembershipTrigger((prev) => prev + 1);
    };

    socket.on("membershipUpdated", handleMembershipUpdate);

    return () => {
      socket.off("membershipUpdated", handleMembershipUpdate);
    };
  }, [socket, userMeta, updateMembershipDetails]);

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <UnProtectedRoute>
              <LoginPage />
            </UnProtectedRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <UnProtectedRoute>
              <SignupPage />
            </UnProtectedRoute>
          }
        />
        <Route
          path="/forgotpassword"
          element={
            <UnProtectedRoute>
              <ForgotPasswordPage />
            </UnProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard membershipTrigger={membershipTrigger} />
            </ProtectedRoute>
          }
        />
      </Routes>

      {/* Toasts */}
      <ToastContainer position="top-right" autoClose={1000} />
    </>
  );
}

export default App;
